import type { DurationInputObject } from 'moment';

export const QUEUE_SIGNALS = {
  REMIND_TODAY: 'remindToday',
  BIRTHDAY_IMMINENT: 'birthdayImminent',
  POLICY_DUE_SOON: 'policyDueSoon',
  BIRTHDAY_THIS_MONTH: 'birthdayThisMonth',
  LONG_TIME_NO_SEE: 'longTimeNoSee'
} as const;

export type QueueSignal = (typeof QUEUE_SIGNALS)[keyof typeof QUEUE_SIGNALS];

/**
 * Sort rank per signal, lowest first. A client appears once, under their first
 * match.
 *
 * `Record<QueueSignal, number>` is what keeps this honest: omitting a signal is
 * a compile error, and a lookup keyed by `QueueSignal` is total — unlike an
 * array `indexOf`, which answers `-1` for an unranked signal and so sorts it
 * first, the least safe default available.
 */
export const SIGNAL_RANK: Record<QueueSignal, number> = {
  [QUEUE_SIGNALS.REMIND_TODAY]: 0,
  [QUEUE_SIGNALS.BIRTHDAY_IMMINENT]: 1,
  [QUEUE_SIGNALS.POLICY_DUE_SOON]: 2,
  [QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH]: 3,
  [QUEUE_SIGNALS.LONG_TIME_NO_SEE]: 4
};

/** Highest priority first, derived from `SIGNAL_RANK` so the two cannot drift. */
export const SIGNAL_PRIORITY: QueueSignal[] = (Object.keys(SIGNAL_RANK) as QueueSignal[]).sort(
  (left, right) => SIGNAL_RANK[left] - SIGNAL_RANK[right]
);

/** 今日重點跟進 only — the queue is a "act on this today" list, so it stays tight. */
export const POLICY_DUE_SOON_THRESHOLD: DurationInputObject = { months: 1 };

/**
 * The 60天續期 stat tile and the list it opens (2026-07-30, at the user's
 * request). Wider than the queue's window on purpose: the tile is a browsing
 * filter — 「which clients should I be preparing for?」 — not a today list, and
 * a renewal conversation starts well before the due date.
 *
 * **60 days, not 2 calendar months, because the tile's label says 60 天.** The
 * `{ months: 2 }` this replaced was 59–62 days depending on the month, so the
 * label would have been wrong roughly half the year. It also has to stay at or
 * under 60: `advDashController`'s policy-due aggregation only fetches policies
 * due within 5256000000ms (≈60.83 days) of now, so a wider window would silently
 * outrun the data — and widening THAT means changing a payload V1's client page
 * and the Business assistant also read.
 */
export const POLICY_DUE_SOON_STAT_THRESHOLD: DurationInputObject = { days: 60 };

export const LONG_TIME_NO_SEE_THRESHOLD: DurationInputObject = { years: 1 };

// v46's "今日重點跟進" deliberately surfaces only the top three priorities.
// The full queue still drives the deterministic ordering before this slice.
export const QUEUE_PREVIEW_SIZE = 3;

/**
 * Validated for CVD separation, normal-vision separation and contrast.
 *
 * 久未聯絡 is violet, not the #3966f8 it was. Nothing was wrong with the blue
 * against the other four — it failed against the PRODUCT. That exact blue is
 * the app's own furniture: every promoted card, every tile glyph, the drawer's
 * header and its call-to-action buttons are painted in it, so a signal wearing
 * it read as another piece of chrome instead of as the one line saying what to
 * do about this client. The other four already sit outside that hue and stood
 * out fine. Violet keeps four separated hues (teal 175° / pink 355° / amber 40°
 * / violet 262°) and clears the chrome. Contrast on white is 5.7:1, and 5.2:1
 * on its own tint below.
 */
export const SIGNAL_COLOR: Record<QueueSignal, string> = {
  [QUEUE_SIGNALS.REMIND_TODAY]: '#0f9b8e',
  [QUEUE_SIGNALS.BIRTHDAY_IMMINENT]: '#e2606a',
  [QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH]: '#e2606a',
  [QUEUE_SIGNALS.POLICY_DUE_SOON]: '#a8730f',
  [QUEUE_SIGNALS.LONG_TIME_NO_SEE]: '#7c3aed'
};

/** Row background tint — the second encoding, so signals are not colour-only. */
export const SIGNAL_TINT: Record<QueueSignal, string> = {
  [QUEUE_SIGNALS.REMIND_TODAY]: '#ecf8f6',
  [QUEUE_SIGNALS.BIRTHDAY_IMMINENT]: '#fdf1f2',
  [QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH]: '#fdf1f2',
  [QUEUE_SIGNALS.POLICY_DUE_SOON]: '#fdf6ea',
  [QUEUE_SIGNALS.LONG_TIME_NO_SEE]: '#f3efff'
};

/** react-intl message id per signal, for the reason text. */
export const SIGNAL_MESSAGE_ID: Record<QueueSignal, string> = {
  [QUEUE_SIGNALS.REMIND_TODAY]: 'clientv2-signal-remind-today',
  [QUEUE_SIGNALS.BIRTHDAY_IMMINENT]: 'clientv2-signal-birthday-imminent',
  [QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH]: 'clientv2-signal-birthday-month',
  [QUEUE_SIGNALS.POLICY_DUE_SOON]: 'clientv2-signal-policy-due',
  [QUEUE_SIGNALS.LONG_TIME_NO_SEE]: 'clientv2-signal-long-time-no-see'
};
